/* eslint-disable react/no-unused-prop-types,react/require-default-props,react/forbid-prop-types,react/default-props-match-prop-types,react/destructuring-assignment,react/prop-types */

// Libraries
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Sticky from 'react-stickynode';
import classnames from 'classnames';
import { selectCurrentInstitutionPath } from '../../../../ReduxStorage/Institutions/CurrentInstitutionPath/selectors';
import { loadable } from '../../loadable';

// Redux
import {
  selectCurrentCourseNavigationLinks,
  selectIsCurrentCourseLoadingNavData,
} from '../../../../ReduxStorage/CourseNavigationInfo/selectors';
import {
  currentCohortPathSelector,
  currentUserIdSelector,
} from '../../../../ReduxStorage/Auth/selectors';
import {
  selectCurrentCoursePath,
  selectIsAdminOfCurrentCourse,
  isCurrentCourseRTLSelector,
  selectCurrentCourseCode,
} from '../../../../ReduxStorage/Courses/selectors';
import { selectIsStaffInCurrentCourse } from '../../../../ReduxStorage/Enrolments/selectors';
import {
  selectCurrentUserIsAssessmentDesignerInCurrentCohort,
  selectCurrentUserIsAssessorInCurrentCohort,
  selectCurrentUserShouldSeeCertificationInCurrentCohort,
  selectGetCertifiedLinkVisible,
} from '../../../../ReduxStorage/CohortRoles/selectors';
import {
  selectOpenModuleSet,
  selectMakeSetCurrentModuleSetAction,
} from '../../../../ReduxStorage/Courses/ModuleDisplay/selectors';
import { addCourseAdmin } from '../../../../ReduxStorage/Courses/dispatchers';
import { selectCurrentUserIsSuperUser } from '../../../../ReduxStorage/Users/selectors';

// Helper components
import { LoadableGetCertifiedLink } from './GetCertified/LoadableGetCertifiedLink';
import { CourseNavigationLinks } from './CourseNavigationLinks/index';
import { CourseNavigationLinksPlaceholder } from './CourseNavigationLinks/CourseNavigationLinksPlaceholder';
import { BecomeAdminButton } from './BecomeAdminButton/index';

// Styles
import './styles.scss';
import { selectBlockPageIsEditing } from '../../../../ReduxStorage/BlockPageUI/simpleSelectors';

// Async loaded components for things that most users never see
// All in the same chunk since lots of it is common
const LoadableCourseAdminLinks = loadable(
  () => import('./CourseAdminLinks/connect'),
  {
    key: 'ConnectedCourseAdminLinksDropdown',
    loading: CourseNavigationLinksPlaceholder,
  }
);

const LoadableCohortAdminLinks = loadable(
  () => import('./CohortAdminLinks/connect'),
  {
    key: 'ConnectedCohortAdminLinksDropdown',
    loading: CourseNavigationLinksPlaceholder,
  }
);

const LoadableCourseCertificationLinks = loadable(
  () => import('./CourseCertificationLinks/connect'),
  {
    key: 'ConnectedCourseCertificationLinks',
    loading: CourseNavigationLinksPlaceholder,
  }
);

const LoadableCourseAssessmentLinks = loadable(
  () => import('./CourseAssessmentLinks/connect'),
  {
    key: 'ConnectedCourseAssessmentLinks',
    loading: CourseNavigationLinksPlaceholder,
  }
);

class _CourseNavigationMenu extends PureComponent {
  static propTypes = {
    coursePath: PropTypes.string,
    cohortPath: PropTypes.string.isRequired,
    courseCode: PropTypes.string.isRequired,
    institutionPath: PropTypes.string,
    currentModuleSet: PropTypes.string,
    navigationLinks: PropTypes.any,
    isLoadingNavigationData: PropTypes.bool.isRequired,
    isFacilitator: PropTypes.bool.isRequired,
    showAssessStudents: PropTypes.bool.isRequired,
    showAssessmentDesign: PropTypes.bool.isRequired,
    showCertification: PropTypes.bool.isRequired,
    makeSetCurrentModuleSetAction: PropTypes.func.isRequired,
    mobile: PropTypes.bool.isRequired,
    isSuperUser: PropTypes.bool.isRequired,
    isCourseAdmin: PropTypes.bool.isRequired,
    addCourseAdmin: PropTypes.func.isRequired,
    isCourseRTL: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    mobile: false,
    coursePath: null,
  };

  componentDidMount() {
    this.positionSidebar();
  }

  setStickyNode = (node) => (this.stickyNode = node);

  positionSidebar = () => {
    requestAnimationFrame(() => {
      if (this.stickyNode) {
        this.stickyNode.updateInitialDimension();
        this.stickyNode.update();
      }
    });
  };

  becomeAdmin = () =>
    this.props.addCourseAdmin({
      userId: this.props.currentUserId,
      coursePath: this.props.coursePath,
    });

  render() {
    if (this.props.isEditingBlockpage) {
      return null;
    }

    const className = classnames('courseNavigationMenu', {
      'dir-right-to-left': this.props.isCourseRTL,
    });

    return (
      // 35 + 45 + 10 = Top bar + course info bar + padding
      <Sticky
        enabled={!this.props.mobile}
        ref={this.setStickyNode}
        top={35 + 45 + 10}
        bottomBoundary=".course-container-box"
      >
        <div className={className}>
          {/* placeholder while navigation links are loading */}
          {this.props.isLoadingNavigationData &&
            this.props.navigationLinks.size === 0 && (
              <CourseNavigationLinksPlaceholder numItems={5} />
            )}
          {(!this.props.isLoadingNavigationData ||
            this.props.navigationLinks.size > 0) && (
            <CourseNavigationLinks
              links={this.props.navigationLinks}
              currentModuleSet={this.props.currentModuleSet}
              makeSetCurrentModuleSetAction={
                this.props.makeSetCurrentModuleSetAction
              }
              courseCode={this.props.courseCode}
              institutionPath={this.props.institutionPath}
            />
          )}
          {this.props.showGetCertified && <LoadableGetCertifiedLink />}
          {this.props.isCourseAdmin && (
            <LoadableCourseAdminLinks onChangeHeight={this.positionSidebar} />
          )}
          {/* TODO: admins should already be facilitators -
              there should be only one condition! */}
          {(this.props.isFacilitator || this.props.isCourseAdmin) && (
            <LoadableCohortAdminLinks onChangeHeight={this.positionSidebar} />
          )}
          {(this.props.showAssessmentDesign ||
            this.props.showAssessStudents) && (
            <LoadableCourseAssessmentLinks
              onChangeHeight={this.positionSidebar}
            />
          )}
          {this.props.showCertification && (
            <LoadableCourseCertificationLinks
              onChangeHeight={this.positionSidebar}
            />
          )}
          {this.props.coursePath &&
            this.props.isSuperUser &&
            !this.props.isCourseAdmin && (
              <BecomeAdminButton becomeAdmin={this.becomeAdmin} />
            )}
        </div>
      </Sticky>
    );
  }
}

const mapStateToProps = (state) => ({
  isEditingBlockpage: selectBlockPageIsEditing(state),
  navigationLinks: selectCurrentCourseNavigationLinks(state),
  isLoadingNavigationData: selectIsCurrentCourseLoadingNavData(state),
  isFacilitator: selectIsStaffInCurrentCourse(state),
  showAssessStudents: selectCurrentUserIsAssessorInCurrentCohort(state),
  showAssessmentDesign: selectCurrentUserIsAssessmentDesignerInCurrentCohort(
    state
  ),
  showCertification: selectCurrentUserShouldSeeCertificationInCurrentCohort(
    state
  ),
  showGetCertified: selectGetCertifiedLinkVisible(state),
  currentModuleSet: selectOpenModuleSet(state),
  makeSetCurrentModuleSetAction: selectMakeSetCurrentModuleSetAction(state),
  isCourseRTL: isCurrentCourseRTLSelector(state),
  coursePath: selectCurrentCoursePath(state),
  cohortPath: currentCohortPathSelector(state),
  courseCode: selectCurrentCourseCode(state),
  institutionPath: selectCurrentInstitutionPath(state),
  isSuperUser: selectCurrentUserIsSuperUser(state),
  isCourseAdmin: selectIsAdminOfCurrentCourse(state),
  currentUserId: currentUserIdSelector(state),
});

const dispatchers = {
  addCourseAdmin,
};

export const CourseNavigationMenu = connect(
  mapStateToProps,
  dispatchers
)(_CourseNavigationMenu);
